import { useEffect, useRef, useState } from 'react';
import { useLocation as useRouterLocation, useNavigate, useParams } from 'react-router-dom';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './AgentsPage.module.css';
import { AgentBuilder } from '../components/AgentBuilder';
import { LoadError } from '../components/LoadError';
import { useCallAgents } from '../hooks/useCallAgents';
import { useCallAgentActions } from '../hooks/useCallAgentActions';
import { useCallAvailableNumbers } from '../hooks/useCallAvailableNumbers';
import { agentToDraft, draftToInput } from '../agentShared';
import type { AutoActivateState, DraftAgent } from '../agentShared';

export function AgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { agents, locationId, isLoading, isError, isSuccess, refetch, isFetching } =
    useCallAgents();
  // A list we can trust to say "this agent doesn't exist": loaded, and not
  // mid-refetch (a cached list from before a create can still be showing).
  const listSettled = isSuccess && !isFetching;
  const actions = useCallAgentActions(locationId);
  const [draft, setDraft] = useState<DraftAgent | null>(null);
  const [cloning, setCloning] = useState(false);
  const { availableNumbers } = useCallAvailableNumbers(agentId);

  const goList = () => navigate('..', { relative: 'path' });

  // Load (or reload, if the URL points at a different agent) the agent from
  // the list. Skipped once a draft for this exact agentId is already loaded
  // so it doesn't clobber in-progress edits on background refetches.
  useEffect(() => {
    // Only judge "not found" from a list that actually loaded. Before the
    // location is known the query is disabled — not loading, empty list —
    // which bounced every refresh/deep link of an agent to the list. A failed
    // load isn't "not found" either.
    if (!isSuccess || !agentId) return;
    const found = agents.find((a) => a.id === agentId);
    if (found) {
      if (!draft || draft.id !== agentId) {
        setDraft(agentToDraft(found));
      }
    } else if (!draft && listSettled) {
      // Never found (bad link, or deleted before this ever loaded) — bounce to the list.
      goList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, agents, isSuccess, listSettled]);

  // If the open agent disappears from the list (e.g. deleted elsewhere), fall
  // back to the list instead of showing stale data.
  useEffect(() => {
    if (agentId && draft && listSettled && !agents.find((a) => a.id === agentId)) {
      goList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, agents, listSettled, draft]);

  const handleSave = () => {
    if (!draft?.id) return;
    const { locationId: _loc, ...updateFields } = draftToInput(draft, locationId);
    void _loc;
    actions.update.mutate({ id: draft.id, data: updateFields });
  };

  // `saveFirst` is off only for the hand-off from the new-agent builder, whose
  // draft was created from these exact values a moment ago.
  const handleActivate = (existingPhoneNumber?: string, saveFirst = true) => {
    if (!draft?.id) return;
    const { locationId: _loc, ...save } = draftToInput(draft, locationId);
    void _loc;
    actions.activate.mutate(
      { id: draft.id, existingPhoneNumber, save: saveFirst ? save : undefined },
      { onSuccess: (activated) => setDraft(agentToDraft(activated)) },
    );
  };

  // Coming from the new-agent builder's "Approve & activate": run it here, once,
  // as soon as the saved agent has loaded, then drop the router state so a
  // refresh/back doesn't re-trigger it.
  const routerLocation = useRouterLocation();
  const autoActivate = routerLocation.state as AutoActivateState | null;
  const autoActivateStarted = useRef(false);
  useEffect(() => {
    if (!autoActivate?.autoActivate || autoActivateStarted.current) return;
    if (!draft?.id || draft.id !== agentId || draft.status === 'active') return;
    autoActivateStarted.current = true;
    navigate('.', { replace: true, state: null });
    handleActivate(autoActivate.existingPhoneNumber, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoActivate, draft?.id, draft?.status, agentId]);

  const handlePause = () => {
    if (!draft?.id) return;
    // Only take the status fields from the server — keep any unsaved edits.
    actions.pause.mutate(draft.id, {
      onSuccess: (paused) =>
        setDraft((prev) =>
          prev
            ? {
                ...prev,
                status: paused.status,
                trackingNumber: paused.trackingNumber,
                fallbackNumber: paused.fallbackNumber,
              }
            : prev,
        ),
    });
  };

  const handleClone = () => {
    if (!draft) return;
    const cloneInput = draftToInput({ ...draft, name: `${draft.name} (copy)` }, locationId);
    setCloning(true);
    actions.create.mutate(cloneInput, {
      onSuccess: (created) => {
        setCloning(false);
        navigate(`../${created.id}`, { replace: true });
      },
      onError: () => setCloning(false),
    });
  };

  const handleDelete = () => {
    if (!draft?.id) return;
    actions.remove.mutate(draft.id, { onSuccess: goList });
  };

  if (isError && !draft) {
    return (
      <div className={patterns.page}>
        <LoadError
          title="Couldn't load this agent"
          onRetry={() => refetch()}
          isRetrying={isFetching}
        />
      </div>
    );
  }

  if (!locationId || isLoading || !draft) {
    return (
      <div className={patterns.page}>
        <div className={styles.loadingSkeleton} />
      </div>
    );
  }

  return (
    <div className={patterns.page}>
      <AgentBuilder
        draft={draft}
        setDraft={(fn) => setDraft((prev) => (prev ? fn(prev) : prev))}
        onBack={goList}
        onSave={handleSave}
        onActivate={handleActivate}
        onPause={handlePause}
        onClone={handleClone}
        onDelete={handleDelete}
        isSaving={actions.update.isPending}
        isActivating={actions.activate.isPending}
        isPausing={actions.pause.isPending}
        isCloning={cloning}
        isDeleting={actions.remove.isPending}
        availableNumbers={availableNumbers}
      />
    </div>
  );
}
